import { DEFAULT_GRID_SETTINGS, DEFAULT_LAYOUT, DEFAULT_SWITCHS, LAYERS, VIEW_MODE } from 'data/static';
import { SetPortIdReturn } from 'store/actions/userActions';
import {
    CloseModel,
    IsAnyOpenedModel,
    Layout,
    OpenModal,
    PilotagePlanSettingType,
    PortType,
    SwitchType,
    TransitType,
} from 'types';
import {
    FavouritControlsInterface,
    GridControlsInterface,
    GridSettingsInterface,
    LayerControlInterface,
    MainControlsInterface,
    MenuControlsInterface,
    MetOceanChartControlsInterface,
    ProfileControlsInterface,
    SearchControlsInterface,
} from 'types/functions';
import { FilterObjectType } from 'types/search';
import { clone } from 'utils/formHandlers';
import { SetPortReturn } from './../actions/userActions';

import { dateTimeFormat } from 'utils/dateTimeFormat';
import { updatePilotagePlanSettings } from 'utils/migrations';
import {
    SetActiveToolsReturn,
    SetDisabledToolsReturn,
    SetGridControlsReturn,
    SetGridSettingsReturn,
    SetHighlightObjectReturn,
    SetLayerControlsReturn,
    SetLayerReturn,
    SetMainControlsReturn,
    SetmenuControlsReturn,
    SetMetOceanChartControlsReturn,
    SetOpendMenuReturn,
    SetOpenModelFunctionReturn,
    SetSearchControlsReturn,
    SetSearchObjectReturn,
    SetSwitchReturn,
    SetSystemReturn,
} from '../actions/layoutActions';
import { LayerType } from './../../types/index.d';
import { SetFavouritControlsReturn } from './../actions/layoutActions';
import { hasPermission } from 'utils/permission';

export interface LayoutState {
    transits: TransitType[];

    portId: null | number;

    switchs: SwitchType;
    gridSettings: GridSettingsInterface;
    pilotagePlanSettings: PilotagePlanSettingType;

    // mode and tabs state
    mode: string;
    layout: Layout;

    searchObject: object;
    system: object;
    openModelFunction: OpenModal;
    isAnyOpenModelFunction: IsAnyOpenedModel;
    closeModelFunction: CloseModel;

    searchControls: SearchControlsInterface;
    favouritControls: FavouritControlsInterface;
    mainControls?: MainControlsInterface;
    gridControls?: GridControlsInterface;
    menuControls?: MenuControlsInterface;
    profileControls?: ProfileControlsInterface;
    layerControls?: LayerControlInterface;
    metOceanChartControls?: MetOceanChartControlsInterface;
    highlightObject?: FilterObjectType;
    selectedTranist: number | string | null;

    layers: LayerType;
}

const defaultPilotagePlanSettings: PilotagePlanSettingType = {
    route: {
        label: 'route',
        comment: 'route',
        value: true,
        options: ['route'],
        group: false,
        loading: false,
        save: true,
    },
    referencePoints: {
        label: 'reference points',
        comment: 'show all reference points',
        value: true,
        options: ['markerPoints', 'wayPoint'],
        group: false,
        loading: false,
        save: true,
    },
    xtd: {
        label: 'xtd',
        comment: 'xtd',
        value: true,
        options: ['XTD'],
        group: false,
        loading: false,
        save: true,
    },
    safetyMargins: {
        label: 'safety margins',
        comment: 'safety margins',
        value: true,
        options: ['safetyMargins'],
        group: false,
        loading: false,
        save: true,
    },

    noGoAreas: {
        label: 'nogoarea',
        comment: 'noGo Areas',
        value: true,
        options: ['noGoAreas'],
        group: false,
        loading: false,
        save: true,
    },
    manoeuvreAreas: {
        label: 'manoeuvreAreas',
        comment: 'Manoeuvre Areas',
        value: true,
        options: ['manoeuvreAreas'],
        group: false,
        loading: false,
        save: true,
    },
};

const getInitial = (port: PortType | null): any => {
    const { pathname } = window.location; // Get the path from the browser's current URL
    const actions = pathname.split('/').filter(Boolean);

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const defaultLayout = DEFAULT_LAYOUT;
    const defaultTab = VIEW_MODE === 'live' ? 'time' : 'chart';
    Object.keys(defaultLayout.sections).forEach((section) => {
        //check permission
        if (defaultLayout.sections[section].checkPermission) {
            defaultLayout.sections[section].display = port?.id
                ? hasPermission(user, port?.id || 0, defaultLayout.sections[section].checkPermission)
                : false;
        }

        
        if (defaultLayout.sections[section].menus.plansMenu && port?.meta_data?.pilotagePlans?.plans.length === 0) {
            defaultLayout.sections[section].menus.plansMenu.active = false;
        }

        if (
            defaultLayout.sections[section].menus.editTransit &&
            (user.type !== 'systemAdministrator' || user.type !== 'portManager')
        ) {
            defaultLayout.sections[section].menus.editTransit.active = false;
        }

        if (
            defaultLayout.sections[section].tools?.['changeTime'] &&
            (section === 'grid' || section === 'chart' || section === 'profile')
        ) {
            defaultLayout.sections[section].tools['changeTime'] = {
                ...defaultLayout.sections[section].tools['changeTime'],
                display: defaultTab === 'chart',
            };
        }

        if (!actions[0]) {
            defaultLayout.sections[section].state = defaultTab === section;
        } else if (!defaultLayout.sections[actions[0]]?.navigate && defaultTab === section) {
            defaultLayout.sections[section].state = true;
            if (VIEW_MODE === 'live' && defaultTab === 'time') {
                defaultLayout.sections[section].loading = true;
            }
        } else if (defaultLayout.sections[actions[0]]?.navigate) {
            defaultLayout.sections[section].state = actions[0] === section;
            defaultLayout.sections[section].display =
                actions[0] === section ? true : defaultLayout.sections[section].display;
            if (VIEW_MODE === 'live' && section === 'time') {
                defaultLayout.sections[section].loading = true;
            }
        }
    });

    // if (VIEW_MODE === 'live') {
    //     const { time, ...rest } = defaultLayout.sections;

    //     defaultLayout.sections = {
    //         time,
    //         ...rest,
    //     };
    // }
    return defaultLayout;
};

const getMode = (): string => {
    const { pathname } = window.location; // Get the path from the browser's current URL

    const actions = pathname.split('/').filter(Boolean);

    switch (actions[0]) {
        case 'chart':
            return 'search';
        case 'grid':
            return 'search';
        case 'playback':
            return 'playback';
        case 'time':
            return 'live';
        default:
            return 'live';
    }
};

const initialState: LayoutState = {
    mode: getMode(),
    layout: getInitial(null),

    transits: [],

    portId: null,
    activeToolData: {},
    searchObject: {},
    //@ts-ignore
    //Ignor init state
    searchControls: {},
    //@ts-ignore
    //Ignor init state
    favouritControls: {},
    //@ts-ignore
    //Ignor init state
    mainControls: {},
    //@ts-ignore
    //Ignor init state
    gridControls: {},
    //@ts-ignore
    //Ignor init state
    profileControls: {},
    //@ts-ignore
    //Ignor init state
    metOceanChartControls: {},
    system: {},
    selectedTranist: null,

    switchs: clone(DEFAULT_SWITCHS),
    gridSettings: clone(DEFAULT_GRID_SETTINGS),
    layers: LAYERS,
    pilotagePlanSettings: defaultPilotagePlanSettings,

    openModelFunction: (e) => {
        return e;
    },
    closeModelFunction: (e) => {
        return e;
    },
};

type LayoutActions =
    | SetPortReturn
    | SetActiveToolsReturn
    | SetOpendMenuReturn
    | SetDisabledToolsReturn
    | SetSwitchReturn
    | SetPortIdReturn
    | SetOpenModelFunctionReturn
    | SetSystemReturn
    | SetSearchObjectReturn
    | SetHighlightObjectReturn
    | SetSearchControlsReturn
    | SetFavouritControlsReturn
    | SetGridControlsReturn
    | SetmenuControlsReturn
    | SetMetOceanChartControlsReturn
    | SetMainControlsReturn
    | SetLayerControlsReturn
    | SetLayerReturn
    | SetGridSettingsReturn;

const deepMerge = <T extends Record<string, any>>(target: T, source: Partial<T>): T => {
    for (const key in source) {
        if (
            source[key] &&
            typeof source[key] === 'object' &&
            //@ts-ignore
            !(source[key] instanceof dateTimeFormat) &&
            !Array.isArray(source[key])
        ) {
            // If the key is an object, recursively merge
            //@ts-ignore
            target[key] = deepMerge(target[key] || {}, source[key] as Partial<T>);
        } else {
            // Otherwise, just update the value
            target[key] = source[key] as T[typeof key];
        }
    }
    return target;
};

const updateLayout = (action: string, layout: Layout, update: any) => {
    switch (action) {
        case 'UPDATE_MODE': {
            return {
                ...layout,
                sections: {
                    ...layout.sections,
                    [update.section]: {
                        ...layout.sections[update.section],
                        mode: update.mode,
                    },
                },
            };
        }
        case 'UPDATE_DATA': {
            return {
                ...layout,
                sections: {
                    ...layout.sections,
                    [update.section]: {
                        ...layout.sections[update.section],
                        data: update.data,
                    },
                },
            };
        }
        case 'ORDER_VIEW': {
            const overrideSections = (originalSections, overrides) => {
                const result = {};
                for (const key of Object.keys(originalSections)) {
                    result[key] = overrides[key]
                        ? { ...originalSections[key], ...overrides[key] }
                        : { ...originalSections[key], state: false };
                }
                return result;
            };

            if (update.viewMode === 'live') {
                return {
                    ...layout,
                    sections: overrideSections(layout.sections, {
                        chart: {
                            ...layout.sections.chart,
                            state: false,
                            tools: {
                                ...layout.sections.chart.tools,
                                changeTime: { ...layout.sections.chart.tools.changeTime, display: false },
                            },
                        },
                        grid: {
                            ...layout.sections.grid,
                            state: false,
                            tools: {
                                ...layout.sections.grid.tools,
                                changeTime: { ...layout.sections.grid.tools.changeTime, display: false },
                            },
                        },
                        profile: {
                            ...layout.sections.profile,
                            state: false,
                            tools: {
                                ...layout.sections.profile.tools,
                                changeTime: { ...layout.sections.profile.tools.changeTime, display: false },
                            },
                        },
                        time: {
                            display: false,
                            state: true,
                            data: {},
                            tools: {
                                ...layout.sections.grid.tools,
                                changeTime: { ...layout.sections.time.tools.changeTime, display: true },
                            },
                        },
                        replay: { display: true, disable: false },
                        playback: {
                            display: false,
                            state: false,
                            data: {},
                            tools: {
                                ...layout.sections.grid.tools,
                                changeTime: { ...layout.sections.playback.tools.changeTime, display: true },
                            },
                        },
                    }),
                    splashScreen: false,
                };
            } else {
                return {
                    ...layout,
                    sections: overrideSections(layout.sections, {
                        chart: {
                            ...layout.sections.chart,
                            state: update.viewMode === 'chart',
                            tools: {
                                ...layout.sections.chart.tools,
                                changeTime: { ...layout.sections.chart.tools.changeTime, display: true },
                            },
                        },
                        grid: {
                            ...layout.sections.grid,
                            state: update.viewMode === 'grid',
                            tools: {
                                ...layout.sections.chart.tools,
                                changeTime: { ...layout.sections.chart.tools.changeTime, display: true },
                            },
                        },
                        profile: {
                            ...layout.sections.profile,
                            state: false,
                            tools: {
                                ...layout.sections.chart.tools,
                                changeTime: { ...layout.sections.chart.tools.changeTime, display: true },
                            },
                        },
                        replay: { display: true, disable: true },
                        time: {
                            display: false,
                            state: false,
                            data: {},
                            tools: {
                                ...layout.sections.grid.tools,
                                changeTime: { ...layout.sections.time.tools.changeTime, display: true },
                            },
                        },
                        playback: {
                            display: false,
                            state: false,
                            data: {},
                            tools: {
                                ...layout.sections.grid.tools,
                                changeTime: { ...layout.sections.playback.tools.changeTime, display: true },
                            },
                        },
                    }),
                    splashScreen: false,
                };
            }
        }
        case 'UPDATE_SECTION': {
            const updatedSections = Object.keys(layout.sections).reduce((acc, key) => {
                acc[key] = {
                    ...layout.sections[key],
                    time: key === update.name && update.state ? Date.now() : layout.sections[key].time,
                    display: key === update.name && 'display' in update ? update.display : layout.sections[key].display,
                    disable: key === update.name && 'disable' in update ? update.disable : layout.sections[key].disable,
                    loading: key === update.name && 'loading' in update ? update.loading : layout.sections[key].loading,
                    state:
                        key === update.name && 'state' in update
                            ? update.state
                            : update.state
                            ? false
                            : layout.sections[key].state, // Set state true for the matched section, false for others
                };
                return acc;
            }, {} as Layout['sections']);

            // Check if any menu in the updated section is true
            const anyMenuActive = Object.values(updatedSections[update.name]?.menus || {}).some((menu) => menu.state);
            const anyToolctive = Object.values(updatedSections[update.name]?.menus || {}).some(
                (menu) => Object.keys(menu.sections).length > 0 && menu.state
            );

            return {
                ...layout,
                sections: updatedSections,
                openMenu: anyMenuActive,
                openTool: anyToolctive,
                splashScreen: false,
            };
        }

        case 'CLOSE_SECTION': {
            // Step 1: Update display and state as per update.name
            const updatedSections = Object.keys(layout.sections).reduce((acc, key) => {
                acc[key] = {
                    ...layout.sections[key],
                    display: key === update.name ? false : layout.sections[key].display,
                    state: key === update.name ? false : layout.sections[key].state,
                };
                return acc;
            }, {} as Layout['sections']);

            // Step 2: Find the section with the maximum `time`
            const maxTimeSectionKey = Object.keys(updatedSections)
                .filter((key) => {
                    return !updatedSections[key].temp && updatedSections[key].display && !updatedSections[key].disable;
                })
                .reduce(
                    (maxKey, key) =>
                        updatedSections[key].time > (updatedSections[maxKey]?.time ?? -Infinity) ? key : maxKey,
                    Object.keys(updatedSections)[0]
                );

            // Step 3: Set `state` to true for the section with max `time`
            const finalSections = Object.keys(updatedSections).reduce((acc, key) => {
                acc[key] = {
                    ...updatedSections[key],
                    display: key === maxTimeSectionKey ? true : updatedSections[key].display,
                    state: key === maxTimeSectionKey ? true : updatedSections[key].state,
                };
                return acc;
            }, {} as Layout['sections']);

            // Check if any menu in the updated section is true
            const anyMenuActive = Object.values(finalSections[maxTimeSectionKey]?.menus || {}).some(
                (menu) => menu.state
            );
            const anyToolctive = Object.values(updatedSections[update.name]?.menus || {}).some(
                (menu) => Object.keys(menu.sections).length > 0 && menu.state
            );

            return {
                ...layout,
                sections: finalSections,
                openMenu: anyMenuActive,
                openTool: anyToolctive,
                splashScreen: false,
            };
        }

        case 'UPDATE_MENU': {
            const updatedMenus = Object.keys(layout.sections[update.section].menus).reduce((acc, menuKey) => {
                acc[menuKey] = {
                    ...layout.sections[update.section].menus[menuKey],
                    state: menuKey === update.name ? update.state : false, // Set target menu state, others false
                    active:
                        update.active === true || update.active === false
                            ? menuKey === update.name
                                ? update.active
                                : layout.sections[update.section].menus[menuKey].active
                            : layout.sections[update.section].menus[menuKey].active,
                    display:
                        update.display === true || update.display === false
                            ? menuKey === update.name
                                ? update.display
                                : layout.sections[update.section].menus[menuKey].display
                            : layout.sections[update.section].menus[menuKey].display,
                };
                return acc;
            }, {} as Layout['sections'][string]['menus']);

            const updatedTools = Object.keys(layout.sections[update.section]?.tools || []).reduce((acc, toolKey) => {
                acc[toolKey] = {
                    ...layout.sections[update.section].tools[toolKey],
                    state: false,
                };
                return acc;
            }, {} as Layout['sections'][string]['tools']);

            // Check if any menu in the section is true
            const anyMenuActive = Object.values(updatedMenus).some((menu) => menu.state);
            const anyToolctive = Object.values(updatedMenus).some(
                (menu) => Object.keys(menu.sections).length > 0 && menu.state
            );
            return {
                ...layout,
                sections: {
                    ...layout.sections,
                    [update.section]: {
                        ...layout.sections[update.section],
                        menus: updatedMenus,
                        tools: updatedTools,
                    },
                },
                ...(update.section !== 'system' && {
                    openMenu: anyMenuActive,
                    openTool: anyToolctive,
                    splashScreen: false,
                }), // Don't update openMenu if section is "system"
            };
        }

        case 'UPDATE_MENU_SECTION': {
            return {
                ...layout,
                sections: {
                    ...layout.sections,
                    [update.section]: {
                        ...layout.sections[update.section],
                        menus: {
                            ...layout.sections[update.section].menus,
                            [update.menu]: {
                                ...layout.sections[update.section].menus[update.menu],
                                sections: Object.keys(
                                    layout.sections[update.section].menus[update.menu].sections
                                ).reduce((acc, sectionKey) => {
                                    const currentSection =
                                        layout.sections[update.section].menus[update.menu].sections[sectionKey];

                                    acc[sectionKey] = {
                                        ...currentSection,
                                        state: sectionKey === update.name ? update.state : false,

                                        // Update selected only if it's explicitly true/false; otherwise, keep the old value
                                        active:
                                            update.active === true || update.active === false
                                                ? sectionKey === update.name
                                                    ? update.active
                                                    : false
                                                : currentSection.active,
                                    };
                                    return acc;
                                }, {} as Layout['sections'][string]['menus'][string]['sections']),
                            },
                        },
                    },
                },
            };
        }
        case 'UPDATE_TOOLS_SECTION': {
            return {
                ...layout,
                sections: {
                    ...layout.sections,
                    [update.section]: {
                        ...layout.sections[update.section],
                        tools: {
                            ...layout.sections[update.section].tools,
                            [update.tool]: {
                                ...layout.sections[update.section].tools[update.tool],
                                sections: Object.keys(
                                    layout.sections[update.section].tools[update.tool].sections
                                ).reduce((acc, sectionKey) => {
                                    const currentSection =
                                        layout.sections[update.section].tools[update.tool].sections[sectionKey];

                                    acc[sectionKey] = {
                                        ...currentSection,
                                        state: sectionKey === update.name ? update.state : false,

                                        // Update selected only if it's explicitly true/false; otherwise, keep the old value
                                        active:
                                            update.active === true || update.active === false
                                                ? sectionKey === update.name
                                                    ? update.active
                                                    : false
                                                : currentSection.active,
                                    };
                                    return acc;
                                }, {} as Layout['sections'][string]['tools'][string]['sections']),
                            },
                        },
                    },
                },
            };
        }

        case 'UPDATE_TOOLS': {
            const updatedTools = Object.keys(layout.sections[update.section].tools)
                .filter((key) => layout.sections[update.section].tools[key].display !== false)
                .reduce((acc, toolKey) => {
                    acc[toolKey] = {
                        ...layout.sections[update.section].tools[toolKey],
                        state: update.names.includes(toolKey) ? update.state : false, // Set target menu state, others false
                        // Update selected only if it's explicitly true/false; otherwise, keep the old value
                        active:
                            update.active === true || update.active === false
                                ? update.names.includes(toolKey)
                                    ? update.active
                                    : layout.sections[update.section].tools[toolKey].active
                                : layout.sections[update.section].tools[toolKey].active,
                        sections: Object.keys(layout.sections[update.section].tools?.[toolKey]?.sections)?.reduce(
                            (scc, sectionKey) => {
                                scc[sectionKey] = {
                                    ...layout.sections[update.section].tools[toolKey].sections[sectionKey],
                                    state: update.names.includes(sectionKey)
                                        ? update.state
                                        : layout.sections[update.section].tools[toolKey].sections[sectionKey].state, // Set target menu state, others false
                                    // Update selected only if it's explicitly true/false; otherwise, keep the old value
                                    active:
                                        update.active === true || update.active === false
                                            ? update.names.includes(sectionKey)
                                                ? update.active
                                                : layout.sections[update.section].tools[toolKey].sections[sectionKey]
                                                      .active
                                            : layout.sections[update.section].tools[toolKey].sections[sectionKey]
                                                  .active,
                                };
                                return scc;
                            },
                            {} as Layout['sections'][string]['tools'][string]['sections']
                        ),
                    };
                    return acc;
                }, {} as Layout['sections'][string]['tools']);

            const updatedMenus = Object.keys(layout.sections[update.section]?.menus || []).reduce((acc, menuKey) => {
                acc[menuKey] = {
                    ...layout.sections[update.section].menus[menuKey],
                    state: update?.keepMenu ? layout.sections[update.section].menus[menuKey].state : false,
                    active:
                        update.active === true || update.active === false
                            ? update.names.includes(menuKey)
                                ? update.active
                                : layout.sections[update.section].menus[menuKey].active
                            : layout.sections[update.section].menus[menuKey].active,
                    sections: Object.keys(layout.sections[update.section].menus[menuKey].sections).reduce(
                        (scc, sectionKey) => {
                            scc[sectionKey] = {
                                ...layout.sections[update.section].menus[menuKey].sections[sectionKey],
                                state: update.names.includes(sectionKey)
                                    ? update.state
                                    : layout.sections[update.section].menus[menuKey].sections[sectionKey].state, // Set target menu state, others false
                                // Update selected only if it's explicitly true/false; otherwise, keep the old value
                                active:
                                    update.active === true || update.active === false
                                        ? update.names.includes(sectionKey)
                                            ? update.active
                                            : layout.sections[update.section].menus[menuKey].sections[sectionKey].active
                                        : layout.sections[update.section].menus[menuKey].sections[sectionKey].active,
                            };
                            return scc;
                        },
                        {} as Layout['sections'][string]['menus'][string]['sections']
                    ),
                };
                return acc;
            }, {} as Layout['sections'][string]['menus']);

            const anyToolctive = Object.values(updatedTools).some(
                (tool) => Object.keys(tool.sections).length > 0 && tool.state
            );

            return {
                ...layout,
                sections: {
                    ...layout.sections,
                    [update.section]: {
                        ...layout.sections[update.section],
                        menus: updatedMenus,
                        tools: updatedTools,
                    },
                },
                ...(update.section !== 'system' && {
                    openMenu: update?.keepMenu ? layout.openMenu : false,
                    openTool: anyToolctive,
                    splashScreen: false,
                }), // Don't update openMenu if section is "system"
            };
        }

        default:
            return deepMerge({ ...layout }, update);
    }
};

const updateLayouts = (layout: Layout, options: any) => {
    options.map((key) => {
        layout = updateLayout(key.action, layout, key.data);
    });

    const section = Object.keys(layout.sections).find((section) => layout.sections[section].state) || 'grid';

    return { layout, mode: layout.sections[section].mode };
};

// Define the reducer
export const layoutReducer = (state: LayoutState = initialState, action: LayoutActions): LayoutState => {
    switch (action.type) {
        case 'SET_DISABLED_TOOLS':
            // @ts-ignore
            return { ...state, disabledTools: [...action.payload] };
        case 'SET_LAYOUT': {
            const { mode, layout } = updateLayouts(state.layout, action.payload);

            return {
                ...state,
                layout,
                mode,
            };
        }
        case 'SET_OPENED_MENU':
            // @ts-ignore
            return { ...state, openMenu: action.payload };
        case 'SET_SYSTEM':
            // @ts-ignore
            return { ...state, system: { ...state.system, ...action.payload } };
        case 'SET_SEARCH_OBJECT':
            // @ts-ignore
            return { ...state, searchObject: action.payload };
        case 'SET_GRID_CONTROLS':
            // @ts-ignore
            return { ...state, gridControls: action.payload };
        case 'SET_VESSEL_CONTROLS':
            // @ts-ignore
            return { ...state, menuControls: { ...state.menuControls, ...action.payload } };
        case 'SET_PROFILE_CONTROLS':
            // @ts-ignore
            return { ...state, profileControls: action.payload };
        case 'SET_LAYER_CONTROLS':
            // @ts-ignore
            return { ...state, layerControls: action.payload };
        case 'SET_METOCEAN_CHART_CONTROLS':
            // @ts-ignore
            return { ...state, metOceanChartControls: action.payload };
        case 'SET_HIGHLIGHT_OBJECT':
            // @ts-ignore
            return { ...state, highlightObject: action.payload };

        case 'SET_PORT': {
            //@ts-ignore
            const port: PortType = action.payload;
            const portId = port.id;

            const storedSwitches = JSON.parse(localStorage.getItem('switchs') || '{}');
            const storedGridSettings = JSON.parse(localStorage.getItem('gridSettings') || '{}');
            const storedLayers = JSON.parse(localStorage.getItem('layers') || '{}');
            const storedPilotagePlanSettings = JSON.parse(localStorage.getItem('pilotagePlanSettings') || '{}');
            console.log(`STORE : Port changed to ${portId}, switches updated`);

            // @ts-ignore
            const portHasPilotagePlans = port.meta_data?.pilotagePlans?.plans?.length >= 1;
            const switchs = storedSwitches[`P-${portId}`] || DEFAULT_SWITCHS;
            const gridSettings = storedGridSettings[`P-${portId}`] || DEFAULT_GRID_SETTINGS;
            const layers = storedLayers[`P-${portId}`] || LAYERS;
            const pilotagePlanSettings = storedPilotagePlanSettings[`P-${portId}`] || defaultPilotagePlanSettings;
            const toBeSavedSwitchs: SwitchType = {}; // Ensure type consistency

            Object.entries(switchs).forEach(([key, swicth]: [string, any]) => {
                // Check if section is 'analysisAreas' and mode matches 'all' or state.mode

                if (swicth.section === 'analysisAreas') {
                    if (
                        !portHasPilotagePlans ||
                        (portHasPilotagePlans && !['route', 'reservedAreas', 'noGoAreas', 'xtd'].includes(key))
                    ) {
                        toBeSavedSwitchs[key] = swicth;
                    }
                } else {
                    toBeSavedSwitchs[key] = swicth;
                }
            });
            console.log(`STORE : update layout complated`);
            return {
                ...state,
                layout: getInitial(port),
                // @ts-ignore
                portId: portId,
                switchs: toBeSavedSwitchs,
                gridSettings: gridSettings,
                layers,
                pilotagePlanSettings: updatePilotagePlanSettings(
                    pilotagePlanSettings,
                    //@ts-ignore
                    action.payload.meta_data?.pilotagePlans?.plans || []
                ),
            };
        }
        case 'SET_SEARCH_CONTROLS':
            // @ts-ignore
            return { ...state, searchControls: action.payload };
        case 'SET_FAVOURIT_CONTROLS':
            // @ts-ignore
            return { ...state, favouritControls: action.payload };
        case 'SET_MAIN_CONTROLS':
            // @ts-ignore
            return { ...state, mainControls: action.payload };
        case 'SET_SELECTED_TRANIST':
            // @ts-ignore
            return { ...state, selectedTranist: action.payload };
        case 'SET_SELECTED_TIME':
            // @ts-ignore
            return { ...state, selectedTime: action.payload };
        case 'SET_SWITCH': {
            // eslint-disable-next-line no-case-declarations
            const switchs = {
                ...state.switchs,
                // @ts-ignore
                [action.payload.key]: {
                    // @ts-ignore
                    ...state.switchs[action.payload.key],
                    // @ts-ignore
                    ...action.payload.value,
                },
            };

            // @ts-ignore
            if (switchs[action.payload.key].save) {
                // Retrieve stored switches from localStorage
                const storedSwitches = JSON.parse(localStorage.getItem('switchs') || '{}');
                if (!storedSwitches[`P-${state.portId}`]) {
                    storedSwitches[`P-${state.portId}`] = {};
                }
                // Loop through each switch in the default settings
                Object.keys(switchs).forEach((key) => {
                    if (switchs[key].save && switchs[key]) {
                        // If the `save` property is TRUE, update the current port switch with default values
                        storedSwitches[`P-${state.portId}`][key] = switchs[key];
                    } else {
                        // only in case if this is a new object
                        storedSwitches[`P-${state.portId}`][key] = DEFAULT_SWITCHS[key];
                    }
                });

                // Save the updated switches back to localStorage
                localStorage.setItem('switchs', JSON.stringify(storedSwitches));
            }

            return { ...state, switchs };
        }
        case 'SET_GRID_SETTINGS': {
            // Retrieve stored gridSettings from localStorage
            const storedGridSettings = JSON.parse(localStorage.getItem('gridSettings') || '{}');
            if (!storedGridSettings[`P-${state.portId}`]) {
                storedGridSettings[`P-${state.portId}`] = {};
            }

            storedGridSettings[`P-${state.portId}`] = action.payload;
            //@ts-ignore
            if (action.save) {
                localStorage.setItem('gridSettings', JSON.stringify(storedGridSettings));
            }
            // @ts-ignore
            return { ...state, gridSettings: action.payload };
        }
        case 'SET_LAYER': {
            // eslint-disable-next-line no-case-declarations
            const layers = {
                ...state.layers,
                // @ts-ignore
                [action.payload.key]: {
                    // @ts-ignore
                    ...state.layers[action.payload.key],
                    // @ts-ignore
                    ...action.payload.value,
                },
            };

            // @ts-ignore

            // Retrieve stored layeres from localStorage
            const storedLayeres = JSON.parse(localStorage.getItem('layers') || '{}');
            if (!storedLayeres[`P-${state.portId}`]) {
                storedLayeres[`P-${state.portId}`] = {};
            }
            // Loop through each layer in the default settings
            Object.keys(layers).forEach((key) => {
                if (layers[key]) {
                    storedLayeres[`P-${state.portId}`][key] = layers[key];
                }
            });

            // Save the updated layeres back to localStorage
            localStorage.setItem('layers', JSON.stringify(storedLayeres));

            return { ...state, layers };
        }
        case 'SET_PILOTAGE_PLAN_SETTING': {
            // eslint-disable-next-line no-case-declarations
            const pilotagePlanSettings = {
                ...state.pilotagePlanSettings,
                // @ts-ignore
                [action.payload.key]: {
                    // @ts-ignore
                    ...state.pilotagePlanSettings[action.payload.key],
                    // @ts-ignore
                    ...action.payload.value,
                },
            };

            // @ts-ignore

            // Retrieve stored PilotagePlanSettinges from localStorage
            const storedPilotagePlanSettinges = JSON.parse(localStorage.getItem('pilotagePlanSettings') || '{}');
            if (!storedPilotagePlanSettinges[`P-${state.portId}`]) {
                storedPilotagePlanSettinges[`P-${state.portId}`] = {};
            }
            // Loop through each PilotagePlanSetting in the default settings
            Object.keys(pilotagePlanSettings).forEach((key) => {
                if (pilotagePlanSettings[key]) {
                    // If the `save` property is TRUE, update the current port PilotagePlanSetting with default values
                    storedPilotagePlanSettinges[`P-${state.portId}`][key] = pilotagePlanSettings[key];
                } else {
                    // only in case if this is a new object
                    storedPilotagePlanSettinges[`P-${state.portId}`][key] = defaultPilotagePlanSettings[key];
                }
            });

            // Save the updated PilotagePlanSettinges back to localStorage
            localStorage.setItem('pilotagePlanSettings', JSON.stringify(storedPilotagePlanSettinges));

            return { ...state, pilotagePlanSettings };
        }
        case 'SET_OPEN_MODEL_FUNCTION':
            // @ts-ignore
            return { ...state, openModelFunction: action.payload };
        case 'SET_IS_OPENED_MODEL_FUNCTION':
            // @ts-ignore
            return { ...state, isAnyOpenModelFunction: action.payload };
        case 'SET_CLOSE_MODEL_FUNCTION':
            // @ts-ignore
            return { ...state, closeModelFunction: action.payload };
        case 'SET_CLOSE_TOOL_FUNCTION':
            // @ts-ignore
            return { ...state, closeToolFunction: action.payload };

        default:
            return state;
    }
};
